import { track } from "@amplitude/analytics-react-native";
import { memo, useCallback, useMemo } from "react";
import { View } from "react-native";
import { SharedValue } from "react-native-reanimated";
import { amp } from "src/api/amplitude";
import { ADDONS, APPLE_MUSIC_ADDON, COMMENTS } from "src/api/featureFlags";
import AddonVariant from "src/api/graphql/__generated__/enums/AddonVariant";
import GatheringAccessEnum from "src/api/graphql/__generated__/enums/GatheringAccessEnum";
import GatheringPrivacy from "src/api/graphql/__generated__/enums/GatheringPrivacy";
import GatheringHeader from "src/components/GatheringHeader";
import { SharedGatheringHeaderProps } from "src/components/GatheringHeader/types";
import { loopMiniCardOrder } from "src/components/MiniCard";
import { MINI_CARD_TITLE_SPACING_TEXT_FILLER } from "src/components/MiniCard/constants";
import parseAddonToMiniCard from "src/components/MiniCard/helpers/parseAddonToMiniCard";
import { MiniCardProps } from "src/components/MiniCard/types";
import MiniCardChatDefaultProps from "src/components/MiniCard/variants/MiniCardChatDefaultProps";
import MiniCardLinkDefaultProps from "src/components/MiniCard/variants/MiniCardLinkDefaultProps";
import MiniCardMoreDefaultProps from "src/components/MiniCard/variants/MiniCardMoreDefaultProps";
import MiniCardRecapDefaultProps from "src/components/MiniCard/variants/MiniCardRecapDefaultProps";
import MiniCardShareDefaultProps from "src/components/MiniCard/variants/MiniCardShareDefaultProps";
import { parseGatheringThumbnailUrl } from "src/constants/GatheringThumbnail";
import { useGatheringContext, useGatheringStore } from "src/contexts/GatheringContext";
import abbreviateBigNumber from "src/helpers/abbreviateBigNumber";
import parseGatheringPreviewTemplateUrl from "src/helpers/parseV0TemplateUrl/parseGatheringPreviewTemplateUrl";
import pluralS from "src/helpers/pluralS";
import useDevice from "src/hooks/useDevice";
import { GatheringOrLocalPhoto } from "src/hooks/useGatheringPhotosWithLocal";
import { useModal } from "src/modals";
import GatheringModal from "src/modals/configs/GatheringModal";
import GlobalModal from "src/modals/configs/GlobalModal";
import { paddingLarge } from "src/styles/spacing";
import { Image, RenderTarget } from "src/swsh-native";
import useGatheringHomePhotoSpacing from "../../hooks/useGatheringHomePhotoSpacing";
import useGatheringShareable from "../../hooks/useGatheringShareable";
import useShareGathering from "../../hooks/useShareGathering";
import GatheringOptionsButton from "../GatheringOptionsButton";
import GatheringUploadPhotosButton from "../GatheringUploadPhotosButton";
import EditableGatheringName from "./components/EditableGatheringName";
import EditThumbnailOverlay from "./components/EditThumbnailOverlay";
import GatheringParticipantsPreview from "./components/GatheringParticipantsPreview";

const renderEndSection = () => {
	return <GatheringUploadPhotosButton sizeVariant="Large" />;
};

const MAX_NUM_ADDONS = 16;
const TEMPLATE_ADDON_VARIANTS = [
	APPLE_MUSIC_ADDON ? AddonVariant.AppleMusic : AddonVariant.Spotify,
	AddonVariant.Location,
	AddonVariant.Venmo,
];
const GatheringHomeHeaderContent = ({
	scrollOffset,
	previewPhotos,
}: {
	scrollOffset: SharedValue<number>;
	previewPhotos: GatheringOrLocalPhoto[];
}) => {
	const privacy = useGatheringStore((state) => state.privacy);
	const numTotalViews = useGatheringStore((state) => state.numTotalViews);
	const gatheringId = useGatheringStore((state) => state.gatheringId);
	const isEditing = useGatheringStore((state) => state.isEditing);
	const numParticipants = useGatheringStore((state) => state.numParticipants);
	const addons = useGatheringStore((state) => state.addons);
	const name = useGatheringStore((state) => state.name);
	const thumbnail = useGatheringStore((state) => state.thumbnail);
	const { presentModal } = useModal<GatheringModal>();
	const { presentModal: presentGlobalModal } = useModal<GlobalModal>();
	const { device } = useDevice();
	const { handleShareGathering } = useShareGathering({
		amplitudeEvent: amp.gathering.shareAlbumPressed,
	});
	const { handleGatheringRecapShare } = useGatheringShareable();
	const hasPhotos = useGatheringStore((state) => state.hasPhotos);
	const context = useGatheringContext();
	const access = useGatheringStore((state) => state.access);
	const numComments = useGatheringStore((state) => state.numComments);
	const hasNewComments = useGatheringStore((state) => state.hasNewComments);

	const { containerHorizontalPadding } = useGatheringHomePhotoSpacing();
	const isHorizontal = device === "desktop";

	const miniCards = useMemo<MiniCardProps[]>(() => {
		const miniCards: MiniCardProps[] = [];

		if (!isEditing) {
			// Comments
			if (COMMENTS && privacy?.includes(GatheringPrivacy.EnableComments)) {
				miniCards.push({
					...MiniCardChatDefaultProps,
					onPress: () => {
						track(amp.gathering.chatPressed, {
							hasNewComments: hasNewComments ?? false,
						});
						presentModal("GatheringCommentsThread", {
							props: {
								gatheringId,
								isAdmin: access === GatheringAccessEnum.Admin,
							},
						});
					},
					title:
						numComments != null && numComments > 0
							? `${abbreviateBigNumber(numComments)} chat${pluralS(numComments)}`
							: "Chat",
					hasNotification: hasNewComments ?? false,
				});
			}

			// Joins/Views
			const showViews =
				privacy?.includes(GatheringPrivacy.FullPublic) && numTotalViews != null;
			const numParticipantsDisplay = Math.max(numParticipants ?? 0, 1);
			const numViewsDisplay = numTotalViews ?? 0;
			miniCards.push({
				title:
					`${abbreviateBigNumber(numParticipantsDisplay)} join${pluralS(numParticipantsDisplay)}` +
					(showViews
						? `${MINI_CARD_TITLE_SPACING_TEXT_FILLER}${abbreviateBigNumber(numViewsDisplay)} view${pluralS(numViewsDisplay)}`
						: ""),
				content: {
					variant: "Render",
					renderContent: () => {
						return <GatheringParticipantsPreview gatheringId={gatheringId} />;
					},
				},
				onPress: () => {
					track(amp.gathering.seeViewersPressed);
					presentModal("GatheringViewers", {
						props: {
							gatheringId,
							context,
						},
					});
				},
				testID: "SeeViewers",
			});
		}

		// Addons
		if (ADDONS) {
			let numAddons = 0;
			const variantsSet = new Set<string>();
			for (const addon of addons ?? []) {
				if (addon !== null && (addon.label || addon.url)) {
					numAddons++;
					if (addon.variant) {
						variantsSet.add(addon.variant);
					}
					const { defaultProps, shouldDisplayAddonCopyModal } =
						parseAddonToMiniCard(addon);
					miniCards.push({
						...defaultProps,
						href: isEditing ? undefined : defaultProps.href,
						variant: isEditing ? "Edit" : "Filled",
						onPress: isEditing
							? () => {
									presentModal("GatheringAddonEditModal", {
										props: {
											gatheringId,
											addonId: addon.addonId,
											variant: addon.variant ?? AddonVariant.Link,
											url: addon.url ?? undefined,
											label: addon.label ?? undefined,
										},
									});
								}
							: () => {
									track(amp.gathering.addon.view, {
										variant: addon.variant,
									});
									if (addon.url && shouldDisplayAddonCopyModal) {
										presentGlobalModal("AddonCopyModal", {
											props: {
												variant: addon.variant ?? AddonVariant.Link,
												text: addon.url,
											},
										});
									}
								},
					});
				}
			}
			if (isEditing && numAddons < MAX_NUM_ADDONS) {
				// Template addons (that are not set)
				const unsetTemplateVariants = TEMPLATE_ADDON_VARIANTS.filter(
					(variant) => !variantsSet.has(variant),
				);

				for (const variant of unsetTemplateVariants) {
					const { defaultProps } = parseAddonToMiniCard({
						variant,
					});

					miniCards.push({
						...defaultProps,
						variant: "Empty",
						onPress: () => {
							presentModal("GatheringAddonEditModal", {
								props: {
									gatheringId,
									variant,
								},
							});
						},
						testID: `AddonTemplate${variant}`,
					});
				}
				miniCards.push({
					...MiniCardLinkDefaultProps,
					variant: "Empty",
					onPress: () => {
						presentModal("GatheringAddonEditModal", {
							props: {
								gatheringId,
								variant: AddonVariant.Link,
							},
						});
					},
					testID: `AddonTemplate${AddonVariant.Link}`,
				});
			}
		}

		if (!isEditing) {
			// Share
			miniCards.push({
				...MiniCardShareDefaultProps,
				onPress: handleShareGathering,
			});

			// Recap
			miniCards.push({
				...MiniCardRecapDefaultProps,
				onPress: handleGatheringRecapShare,
			});

			// Options
			miniCards.push({
				...MiniCardMoreDefaultProps,
				renderContent: (children) => {
					return (
						<GatheringOptionsButton
							style={{
								width: "100%",
								height: "100%",
								alignItems: "center",
							}}
						>
							{children}
						</GatheringOptionsButton>
					);
				},
			});
		}

		return isHorizontal ? miniCards : loopMiniCardOrder(miniCards);
	}, [
		addons,
		context,
		gatheringId,
		handleGatheringRecapShare,
		handleShareGathering,
		isEditing,
		numParticipants,
		numTotalViews,
		presentModal,
		presentGlobalModal,
		privacy,
		access,
		numComments,
		hasNewComments,
		isHorizontal,
	]);
	const renderWheelItem = useCallback<
		NonNullable<SharedGatheringHeaderProps<GatheringOrLocalPhoto>["renderWheelItem"]>
	>(({ item, width, height, opacity, borderRadius }) => {
		const src = parseGatheringPreviewTemplateUrl({
			readUrl: item.readUrl,
		});
		if (!src) return null;
		return (
			<Image
				alt="Photo"
				src={src}
				dim={{
					width,
					height,
				}}
				style={{
					borderRadius,
					overflow: "hidden",
					opacity,
				}}
			/>
		);
	}, []);

	const regularOptions = useMemo(() => {
		return {
			autoPlay: !isEditing,
			scrollOffset,
		};
	}, [scrollOffset, isEditing]);
	const wideOptions = useMemo(() => {
		return {
			horizontalPadding: containerHorizontalPadding,
		};
	}, [containerHorizontalPadding]);

	return (
		<>
			<GatheringHeader
				testID="GatheringHomeHeader"
				wheelData={previewPhotos}
				renderWheelItem={renderWheelItem}
				name={name}
				thumbnailUrl_1x1={parseGatheringThumbnailUrl({ readUrl: thumbnail?.readUrl })}
				variant={isHorizontal ? "Wide" : "Regular"}
				regularOptions={regularOptions}
				wideOptions={wideOptions}
				miniCards={miniCards}
				renderThumbnailOverlay={
					isEditing
						? () => {
								return <EditThumbnailOverlay />;
							}
						: undefined
				}
				renderName={
					isEditing
						? ({ fontSize, name, textStyle }) => {
								return (
									<EditableGatheringName
										defaultValue={name}
										isHorizontal={isHorizontal}
										iconSize={0.7 * fontSize}
										textStyle={textStyle}
									/>
								);
							}
						: undefined
				}
				renderEndSection={device === "desktop" && hasPhotos ? renderEndSection : undefined}
			/>
		</>
	);
};
const MemoedGatheringHomeHeaderContent = memo(GatheringHomeHeaderContent);

const GatheringHomeHeader = ({
	target,
	scrollOffset,
	previewPhotos,
}: {
	target: RenderTarget;
	scrollOffset: SharedValue<number>;
	previewPhotos: GatheringOrLocalPhoto[];
}) => {
	const headerHeight = useGatheringStore((state) => state.headerHeight);
	const setHeaderHeight = useGatheringStore((state) => state.setHeaderHeight);

	return (
		<View
			onLayout={(e) => {
				if (e.nativeEvent.layout.height > 0) {
					setHeaderHeight(e.nativeEvent.layout.height);
				}
			}}
			style={{
				paddingBottom: paddingLarge,
				width: "100%",
				justifyContent: "center",
				alignItems: "center",
				height: target === "Measurement" ? headerHeight : undefined,
			}}
		>
			{target === "Measurement" ? null : (
				<MemoedGatheringHomeHeaderContent
					scrollOffset={scrollOffset}
					previewPhotos={previewPhotos}
				/>
			)}
		</View>
	);
};
export default GatheringHomeHeader;
